import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import { apiFailureLine, classifyApiFailure, type ApiFailure } from '@/teacher-request/apiFailure'
import type { ApiError as ApiErrorType } from '@/types/api'
import type { Role } from '@/types/role'
import type { User } from '@/types/auth'
import { isPublicAuthPath, isSafeInternalPath } from '@/utils/authRedirect'
import { clearTokens, getAccessToken, getRefreshToken, setTokens } from '@/utils/tokenStorage'

function redirectToLogin(): void {
  const { pathname, search } = window.location
  if (isPublicAuthPath(pathname)) return
  const next = `${pathname}${search}`
  const target = isSafeInternalPath(next)
    ? `/login?next=${encodeURIComponent(next)}`
    : '/login'
  window.location.href = target
}

export const API_BASE_URL = import.meta.env.VITE_API_URL || ''

export function isSessionExchangeUrl(url: string): boolean {
  const path = String(url || '').split('?')[0] ?? ''
  return (
    /\/auth\/verify(?:-code|-prep-token|-substitution-token|-admin-token)?$/.test(path) ||
    /\/auth\/webauthn\/authenticate$/.test(path) ||
    /\/email\/actions\/[^/]+\/login$/.test(path)
  )
}

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
})

const REFRESH_TIMEOUT_MS = 15000
const REFRESH_WAIT_MS = 20000

let isRefreshing = false
let refreshInFlight: Promise<string | null> | null = null
let refreshSubscribers: Array<(token: string | null) => void> = []

function subscribeTokenRefresh(cb: (token: string | null) => void) {
  refreshSubscribers.push(cb)
}

function onRefreshed(token: string | null) {
  refreshSubscribers.forEach(cb => cb(token))
  refreshSubscribers = []
}

export function userPatchFromAccessPayload(
  user: Pick<User, 'role' | 'engineer_scopes' | 'school_id' | 'acting_teacher_id' | 'acting_teacher_name' | 'acting_school_name' | 'acting_role' | 'acting_read_only' | 'acting_display_name' | 'acting_partner_name' | 'acting_user_id' | 'acting_partner_company_id'>,
  payload: {
    role?: string
    engineer_scopes?: string[]
    school_id?: string | null
    acting_teacher_id?: string | null
    acting_role?: string | null
    acting_read_only?: boolean
    acting_user_id?: string | null
    acting_partner_company_id?: string | null
  },
) {
  const own = (key: string) => Object.prototype.hasOwnProperty.call(payload, key)
  const actingTeacher = own('acting_teacher_id') ? payload.acting_teacher_id ?? null : user.acting_teacher_id
  const actingRole = own('acting_role') ? (payload.acting_role as Role | null) ?? null : user.acting_role ?? null
  const actingUser = own('acting_user_id') ? payload.acting_user_id ?? null : user.acting_user_id ?? null
  const actingPartner = own('acting_partner_company_id')
    ? payload.acting_partner_company_id ?? null
    : user.acting_partner_company_id ?? null
  const school = own('school_id') ? payload.school_id ?? null : user.school_id
  const overlay = Boolean(actingRole || actingTeacher || actingUser || actingPartner)
  return {
    role: overlay ? ((actingRole || user.role) as Role) : ((payload.role as Role) || user.role),
    engineer_scopes: payload.engineer_scopes ?? (overlay ? user.engineer_scopes : []),
    school_id: school,
    acting_teacher_id: overlay ? actingTeacher : null,
    acting_user_id: overlay ? actingUser : null,
    acting_partner_company_id: overlay ? actingPartner : null,
    acting_teacher_name: overlay ? user.acting_teacher_name : null,
    acting_school_name: overlay ? user.acting_school_name : null,
    acting_role: overlay ? actingRole : null,
    acting_read_only: overlay ? (payload.acting_read_only ?? user.acting_read_only ?? false) : false,
    acting_display_name: overlay ? user.acting_display_name : null,
    acting_partner_name: overlay ? user.acting_partner_name : null,
  }
}

function patchUserFromAccessToken(token: string): void {
  try {
    const parts = token.split('.')
    if (!parts[1]) return
        const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))) as {
      role?: string
      engineer_scopes?: string[]
      school_id?: string | null
      acting_teacher_id?: string | null
      acting_role?: string | null
      acting_read_only?: boolean
      acting_user_id?: string | null
      acting_partner_company_id?: string | null
    }
    void import('@/store/authStore').then(({ useAuthStore }) => {
      const user = useAuthStore.getState().user
      if (!user) return
      useAuthStore.setState({
        user: {
          ...user,
          ...userPatchFromAccessPayload(user, payload),
        },
      })
    })
  } catch {
    /* ignore malformed JWT */
  }
}

function getTokenExpiration(token: string): number | null {
  try {
    const parts = token.split('.')
    if (!parts[1]) return null
    const payload = JSON.parse(atob(parts[1]))
    return payload.exp ? payload.exp * 1000 : null
  } catch {
    return null
  }
}

export function shouldClearSessionOnRefreshError(error: unknown): boolean {
  const status =
    (error as { response?: { status?: number }; statusCode?: number })?.response?.status
    ?? (error as { statusCode?: number }).statusCode
  return status === 401 || status === 403
}

export function refreshAccessToken(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight
  const refreshToken = getRefreshToken()
  if (!refreshToken) return Promise.resolve(null)

  refreshInFlight = (async () => {
    try {
      const response = await axios.post(`${API_BASE_URL}/api/v1/auth/refresh`, {
        refresh_token: refreshToken,
      }, { timeout: REFRESH_TIMEOUT_MS })
      const { access_token, refresh_token: newRefreshToken } = response.data
      setTokens(access_token, newRefreshToken)
      return access_token as string
    } catch (error) {
      if (shouldClearSessionOnRefreshError(error)) {
        clearTokens()
      }
      return null
    } finally {
      refreshInFlight = null
    }
  })()
  return refreshInFlight
}

apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      const headers = config.headers as { delete?: (key: string) => void } & Record<string, unknown>
      if (typeof headers.delete === 'function') headers.delete('Content-Type')
      else delete (config.headers as Record<string, unknown>)['Content-Type']
    }
    const reqUrl = String(config.url || '')
    if (reqUrl.includes('/auth/refresh')) return config
    if (isSessionExchangeUrl(reqUrl)) return config

    const token = getAccessToken()
    if (!token) return config

    const expiration = getTokenExpiration(token)
    if (!expiration) {
      config.headers.Authorization = `Bearer ${token}`
      return config
    }

    const now = Date.now()
    const fiveMinutes = 5 * 60 * 1000

    if (expiration - now < fiveMinutes && !isRefreshing) {
      isRefreshing = true
      try {
        const newToken = await refreshAccessToken()
        if (newToken) {
          config.headers.Authorization = `Bearer ${newToken}`
          onRefreshed(newToken)
          patchUserFromAccessToken(newToken)
        } else if (isPublicAuthPath(window.location.pathname)) {
          delete config.headers.Authorization
          onRefreshed(null)
          return config
        } else {
          onRefreshed(null)
          if (!getRefreshToken()) redirectToLogin()
          return Promise.reject(new Error('Session expired'))
        }
      } catch (err) {
        onRefreshed(null)
        if (isPublicAuthPath(window.location.pathname)) {
          delete config.headers.Authorization
          return config
        }
        throw err
      } finally {
        isRefreshing = false
      }
    } else if (isRefreshing) {
      if (isPublicAuthPath(window.location.pathname)) {
        delete config.headers.Authorization
        return config
      }
      return new Promise((resolve, reject) => {
        let settled = false
        const timer = window.setTimeout(() => {
          if (settled) return
          settled = true
          reject(new Error('Session refresh timeout'))
        }, REFRESH_WAIT_MS)
        subscribeTokenRefresh((newToken) => {
          if (settled) return
          settled = true
          window.clearTimeout(timer)
          if (!newToken) {
            reject(new Error('Session refresh failed'))
            return
          }
          config.headers.Authorization = `Bearer ${newToken}`
          resolve(config)
        })
      })
    } else {
      config.headers.Authorization = `Bearer ${token}`
    }

    return config
  },
  (error) => Promise.reject(error)
)

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiErrorType>) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          let settled = false
          const timer = window.setTimeout(() => {
            if (settled) return
            settled = true
            reject(error)
          }, REFRESH_WAIT_MS)
          subscribeTokenRefresh((token) => {
            if (settled) return
            settled = true
            window.clearTimeout(timer)
            if (!token) {
              reject(error)
              return
            }
            originalRequest.headers.Authorization = `Bearer ${token}`
            resolve(apiClient(originalRequest))
          })
        })
      }

      isRefreshing = true
      try {
        const newToken = await refreshAccessToken()
        if (newToken) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`
          onRefreshed(newToken)
          patchUserFromAccessToken(newToken)
          return apiClient(originalRequest)
        }
        onRefreshed(null)
      } catch (err) {
        onRefreshed(null)
        throw err
      } finally {
        isRefreshing = false
      }
    }

    if (error.response?.status === 401 && !isPublicAuthPath(window.location.pathname)) {
      if (!getRefreshToken()) {
        clearTokens()
        redirectToLogin()
      }
    }
    return Promise.reject(error)
  }
)

export class ApiErrorResponse extends Error {
  constructor(
    public statusCode: number,
    public detail: string,
    public response?: AxiosResponse,
    public failure?: ApiFailure,
  ) {
    super(detail)
    this.name = 'ApiErrorResponse'
  }
}

export class CancelledApiCall extends Error {
  constructor(public failure?: ApiFailure) {
    super('')
    this.name = 'CancelledApiCall'
  }
}

function failureFromAxios(error: AxiosError): ApiFailure {
  const cancelled = axios.isCancel(error) || error.code === 'ERR_CANCELED' || error.code === 'ERR_CANCELLED'
  let code = error.code ?? null
  if (cancelled) code = 'ERR_CANCELED'
  else if (!error.response && /timeout/i.test(error.message)) code = code === 'ETIMEDOUT' ? code : 'ECONNABORTED'
  return {
    method: error.config?.method ?? '',
    address: error.config?.url ?? '',
    status: error.response?.status ?? null,
    code,
    body: error.response?.data ?? null,
  }
}

export async function handleApiRequest<T>(
  request: Promise<AxiosResponse<T>>
): Promise<T> {
  try {
    const response = await request
    return response.data
  } catch (error) {
    if (error instanceof AxiosError) {
      const failure = failureFromAxios(error)
      const sentence = classifyApiFailure(failure)
      if (sentence == null) throw new CancelledApiCall(failure)
      const line = apiFailureLine(failure)
      if (line) console.error(line)
      throw new ApiErrorResponse(
        error.response?.status ?? 0,
        sentence,
        error.response,
        failure,
      )
    }
    throw error
  }
}
